import { MenuProduct } from '../services/MenuService';

/**
 * Get recommended products based on the current product being viewed
 *
 * Recommendation logic:
 * 1. Show products from the same category as the current product
 * 2. Exception: If viewing Accessories/Apparel, show Flower products instead
 * 3. Sort by lowest quantity available (scarcity/urgency)
 * 4. Tie-breaker: If quantities match, show most expensive products
 * 5. Exclude the current product from recommendations
 * 6. Exclude products already in the cart
 *
 * @param currentProduct The product currently being viewed
 * @param allProducts All available products from the menu
 * @param limit Maximum number of recommendations to return (default: 4)
 * @param cart Optional cart object to exclude products already added
 * @returns Array of recommended products
 */
export function getRecommendedProducts(
  currentProduct: MenuProduct,
  allProducts: MenuProduct[],
  limit: number = 4,
  cart?: any
): MenuProduct[] {
  if (!currentProduct || !allProducts || allProducts.length === 0) {
    return [];
  }

  // Determine target category for recommendations
  const currentCategory = currentProduct.category?.toLowerCase() || '';
  const isAccessoryOrApparel =
    currentCategory === 'accessories' ||
    currentCategory === 'accessory' ||
    currentCategory === 'apparel';

  // If viewing accessories/apparel, recommend from flower category instead
  const targetCategory = isAccessoryOrApparel ? 'flower' : currentCategory;

  // Helper function to get total quantity available for a product
  const getTotalQuantity = (product: MenuProduct): number => {
    if (!product.variants || product.variants.length === 0) return 0;

    return product.variants.reduce((total, variant) => {
      return total + (variant.quantity || 0);
    }, 0);
  };

  // Helper function to check if product is in cart
  const isProductInCart = (product: MenuProduct): boolean => {
    if (!cart || !cart.items || !Array.isArray(cart.items)) return false;
    return cart.items.some((item: any) => item.productId === product.id);
  };

  // Filter products by target category, exclude current product, exclude cart items, and require minimum quantity of 3
  const categoryProducts = allProducts.filter(product => {
    if (product.id === currentProduct.id) return false; // Exclude current product
    if (isProductInCart(product)) return false; // Exclude products already in cart
    const productCategory = product.category?.toLowerCase() || '';
    if (productCategory !== targetCategory) return false;

    // Require minimum total quantity of 3
    const totalQuantity = getTotalQuantity(product);
    return totalQuantity >= 3;
  });

  // Helper function to get highest price for a product (for tie-breaking)
  const getHighestPrice = (product: MenuProduct): number => {
    if (!product.variants || product.variants.length === 0) return 0;

    const prices = product.variants
      .map(v => v.priceRec || v.priceMed || 0)
      .filter(price => price > 0);

    return prices.length > 0 ? Math.max(...prices) : 0;
  };

  // Sort by lowest quantity first, then by highest price for tie-breaking
  const sortedProducts = categoryProducts.sort((a, b) => {
    const qtyA = getTotalQuantity(a);
    const qtyB = getTotalQuantity(b);

    // Primary sort: lowest quantity first
    if (qtyA !== qtyB) {
      return qtyA - qtyB;
    }

    // Tie-breaker: highest price first
    const priceA = getHighestPrice(a);
    const priceB = getHighestPrice(b);
    return priceB - priceA;
  });

  // Return limited number of recommendations
  return sortedProducts.slice(0, limit);
}